import { useState, useEffect } from 'react';

// Global auth token utility: keeps the token in-memory and in localStorage

let inMemoryToken: string | null = null; // raw token without Bearer

function normalizeRawToken(token: string | null): string | null {
  if (!token) return null;
  return token.replace(/^Bearer\s+/i, '').trim() || null;
}

export function setAuthToken(token: string | null): void {
  inMemoryToken = normalizeRawToken(token);
  if (inMemoryToken) {
    localStorage.setItem('token', inMemoryToken);
  } else {
    localStorage.removeItem('token');
  }
}

export function getRawAuthToken(): string | null {
  if (inMemoryToken) return inMemoryToken;
  const stored = localStorage.getItem('token');
  inMemoryToken = normalizeRawToken(stored);
  return inMemoryToken;
}

export function getAuthToken(): string {
  const raw = getRawAuthToken();
  return raw ? `Bearer ${raw}` : '';
}

export function clearAuthToken(): void {
  inMemoryToken = null;
  localStorage.removeItem('token');
}

// Web-specific token validation utilities
export const isWebPlatform = (): boolean => {
  return typeof window !== 'undefined' && !window.Capacitor?.isNativePlatform();
};

export const isMobilePlatform = (): boolean => {
  return typeof window !== 'undefined' && window.Capacitor?.isNativePlatform();
};

// Simplified token validation for web - no refresh, just check expiration
export const validateTokenForWeb = async (): Promise<{ isValid: boolean; token: string | null; username: string | null }> => {
  try {
    const savedToken = localStorage.getItem('token');
    const savedUsername = localStorage.getItem('username');
    
    if (!savedToken || !savedUsername) {
      console.log('Web: No saved token or username found');
      return { isValid: false, token: null, username: null };
    }

    console.log('Web: Validating saved token...');
    
    // Import dynamically to avoid circular dependencies
    const { isTokenExpired } = await import('./api');
    
    // Check if token is expired
    if (isTokenExpired()) {
      console.log('Web: Token is expired, clearing auth data');
      // Clear all auth data
      clearAuthToken();
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('username');
      return { isValid: false, token: null, username: null };
    } else {
      console.log('Web: Token is valid, proceeding with saved session');
      // Token is valid, proceed with saved session
      const authToken = getAuthToken();
      return { isValid: true, token: authToken, username: savedUsername };
    }
  } catch (error) {
    console.error('Web: Error validating token:', error);
    // Clear auth data on error
    clearAuthToken();
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('username');
    return { isValid: false, token: null, username: null };
  }
};

// Global token validation hook for any component
export const useTokenValidation = () => {
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      const result = await validateTokenForWeb();
      setIsValid(result.isValid);
      setToken(result.token);
      setUsername(result.username);
      setIsValidating(false);
    };

    validateToken();
  }, []);

  return { isValidating, isValid, token, username };
};


